import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ProgressBar from '@atlassian/jira-common-components-progress-bar/src/main.tsx';
import ProgressElement from '@atlassian/jira-common-components-progress-bar/src/progress-element/index.tsx';
import {
	IN_PROGRESS,
	DONE,
	statusCategoryForId,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { progressSummary_issueViewChildIssuesPanel$key } from '@atlassian/jira-relay/src/__generated__/progressSummary_issueViewChildIssuesPanel.graphql';
import messages from './messages.tsx';

export interface Props {
	issues: progressSummary_issueViewChildIssuesPanel$key | null;
	isClassicSubtaskPanel: boolean;
}

export const CLASSIC_SUBTASK_PANEL_TOOLTIP = {
	remainingTooltip: messages.todoSubtasksSegmentTooltipText,
	completedTooltip: messages.completedSubtasksSegmentTooltipText,
	inProgressTooltip: messages.inProgressSubtasksSegmentTooltipText,
} as const;

export const CHILD_ISSUE_PANEL_TOOLTIP_ISSUE_TERM_REFRESH = {
	remainingTooltip: messages.todoIssuesSegmentTooltipTextIssueTermRefresh,
	completedTooltip: messages.completedIssuesSegmentTooltipTextIssueTermRefresh,
	inProgressTooltip: messages.inProgressIssuesSegmentTooltipTextIssueTermRefresh,
} as const;

export const CHILD_ISSUE_PANEL_TOOLTIP = {
	remainingTooltip: messages.todoIssuesSegmentTooltipText,
	completedTooltip: messages.completedIssuesSegmentTooltipText,
	inProgressTooltip: messages.inProgressIssuesSegmentTooltipText,
} as const;

export const ProgressSummary = ({ issues, isClassicSubtaskPanel }: Props) => {
	const { formatMessage } = useIntl();

	const CHILD_ISSUE_PANEL_TOOLTIP_MESSAGE = fg('jira-issue-terminology-refresh-m3')
		? CHILD_ISSUE_PANEL_TOOLTIP_ISSUE_TERM_REFRESH
		: CHILD_ISSUE_PANEL_TOOLTIP;

	const { remainingTooltip, completedTooltip, inProgressTooltip } = isClassicSubtaskPanel
		? CLASSIC_SUBTASK_PANEL_TOOLTIP
		: CHILD_ISSUE_PANEL_TOOLTIP_MESSAGE;

	const childWorkProgressSummaryLabel = fg('jira-issue-terminology-refresh-m3')
		? messages.childWorkProgressSummaryLabelRefresh
		: messages.childWorkProgressSummaryLabel;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const statusData = graphql`
		fragment progressSummary_issueViewChildIssuesPanel_statusAndResolved on JiraIssue
		@argumentDefinitions(
			isIssueViewChildIssuesPanelRealtimeUpdateEnabled: {
				type: "Boolean!"
				provider: "@atlassian/jira-relay-provider/src/jira-issue-view-child-issues-panel-realtime-update.relayprovider"
			}
		) {
			# eslint-disable-next-line @atlassian/relay/unused-fields
			isResolved @include(if: $isIssueViewChildIssuesPanelRealtimeUpdateEnabled)
			statusField @include(if: $isIssueViewChildIssuesPanelRealtimeUpdateEnabled) {
				status {
					statusCategory {
						statusCategoryId
					}
				}
			}
		}
	`;

	const data = useFragment<progressSummary_issueViewChildIssuesPanel$key>(
		graphql`
			fragment progressSummary_issueViewChildIssuesPanel on JiraIssueConnection {
				edges {
					node {
						...progressSummary_issueViewChildIssuesPanel_statusAndResolved
						statusField {
							status {
								statusCategory @required(action: LOG) {
									statusCategoryId
								}
							}
						}
					}
				}
			}
		`,
		issues,
	);
	const edges = data?.edges || [];

	const totalIssueCount = edges.length;

	const completedIssueCount = edges.filter(
		(e) =>
			statusCategoryForId(e?.node?.statusField?.status?.statusCategory.statusCategoryId) === DONE,
	).length;

	const inProgressIssueCount = edges.filter(
		(e) =>
			statusCategoryForId(e?.node?.statusField?.status?.statusCategory.statusCategoryId) ===
			IN_PROGRESS,
	).length;

	// Note: on rare occasions, it is possible for todoIssueCount to include issues with UNDEFINED status category.
	const todoIssueCount = totalIssueCount - inProgressIssueCount - completedIssueCount;

	// Math.floor instead of Math.round so we don't incorrectly show 100% Done when we're only at 99.5%.
	const completionPercentage = Math.floor((completedIssueCount / totalIssueCount) * 100);

	return (
		<Inline alignBlock="center" alignInline="center" grow="fill">
			<Box xcss={progressBarContainerStyles}>
				<ProgressBar
					sum={totalIssueCount}
					backgroundColor={token('color.chart.neutral')}
					remainingTooltipContent={formatMessage(remainingTooltip, {
						todoIssueCount,
						totalIssueCount,
					})}
					height={8}
					aria-label={formatMessage(childWorkProgressSummaryLabel)}
				>
					<ProgressElement
						value={completedIssueCount}
						color={token('color.border.success')}
						tooltipContent={formatMessage(completedTooltip, {
							completedIssueCount,
							totalIssueCount,
						})}
					/>
					<ProgressElement
						value={inProgressIssueCount}
						color={token('color.border.information')}
						tooltipContent={formatMessage(inProgressTooltip, {
							inProgressIssueCount,
							totalIssueCount,
						})}
					/>
				</ProgressBar>
			</Box>
			<Box
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(fg('jfp_a11y_fix_announce_updated_progress')
					? {
							'aria-live': 'polite',
							'aria-atomic': 'true',
						}
					: {
							'aria-hidden': 'true',
						})}
				testId="issue-view-child-issues-panel.ui.progress-summary.progress-percentage-container"
				xcss={completionPercentageContainerStyles}
			>
				<FormattedMessage
					{...messages.completionPercentageText}
					values={{
						completionPercentage: (
							<Box as="span" xcss={percentageValueContainer}>
								{completionPercentage}
							</Box>
						),
					}}
				/>
			</Box>
		</Inline>
	);
};

const completionPercentageContainerStyles = xcss({
	color: 'color.text.subtlest',
	flexShrink: 0,
	marginLeft: 'space.200',
	marginRight: 'space.200',
});

const percentageValueContainer = xcss({
	display: 'inline-block',
});

const progressBarContainerStyles = xcss({
	flexGrow: 1,
});
